import Vue from "vue";
import Vuex from 'vuex';
import getters from './getters'; // Pull data from our store
import mutations from "./mutations.js"; // how to update our store
import actions from "./actions"; // invoke a mutation to update the store
import watchers from "./watchers";
import { schema } from "../schema.js"
import { newApplication } from "@/schema.js";
import moment from "moment";
import createPersistedState from 'vuex-persistedstate';
import journey from "./modules/journey.js";
import notifications from "./modules/notifications.js";


Vue.use(Vuex);

const authenticated = schema.authenticated;

// IMPORTANT: add state variables to default_state and list their keys inreset_keys_on_events where appropriate
// such that those state variables get reset on for example a user login so that a user does not see a previous users cached data

const options = {
    'address_history_ownership_options': [],
    'address_type': [],
    'address_street_type': [],
    'adjustment_types': [],
    'adp_products': [],
    'all_billing_types': [],
    'all_tier_names': [],
    'applicant_type': [],
    'app_lead_type': [],
    'app_status_reason_type': [],
    'app_status_type': [],
    'assets_sale_types': [],
    'asset_type_options': [],
    'broker_employment_type': [],
    'broker_pods': [],
    'countries': [],
    'country_states': [],
    'document_templates': [],
    'document_types': [],
    'employment_occupation': [],
    'employment_type_options': [],
    'expense_type': [],
    'gender': [],
    'hubspot_branding_type': [],
    'hubspot_loan_purpose': [],
    'identification_type': [],
    'income_type': [],
    'insurance_provider': [],
    'insurance_types': [],
    'introducer_details': [],
    'lenders': [],
    'lender_finance_one_name': [],
    'license_type': [],
    'loan_application_type_options': [],
    'loan_asset_type': [],
    'marketing_source_type': [],
    'model_years': [],
    'payment_frequency_options': [],
    'personal_details_marital_status_options': [],
    'personal_details_residency_status': [],
    'phone_type': [],
    'privacy_consent_type_options': [],
    'products_options': [],
    'roles': [],
    'settlement_expense_types': [],
    'submission_repayment_type': [],
    'submission_status_options': [],
    'user_pods': [],
    'user_roles_types': [],
    'user_type': [],
    'zink_services': [],
    'asset_finance_provider': [],
    'reference_contact_hours': [],
    'reference_relationship': [],
    'asset_vehicle_body_type': [],
    'employment_industry': [],
};

const properties = {
    'address_history_ownership_properties': {},
    'asset_type_properties': {},
    'auction_service_billings': {},
    'capacity_rules': {},
    'clawback_types': {},
    'communication_templates': {},
    'employment_type_properties': {},
    'loan_application_type_properties': {},
    'payment_frequency_properties': {},
    'personal_details_marital_status_properties': {},
    'privacy_consent_type_properties': {},
    'products_properties': {},
    'submission_status_properties': {},
};

// defines the default state and the variables in state
// good practice: use null as default so that we can check for data availability
// Using null is only preferable if you want to explicitly represent an uninitialized or missing value, which requires more cautious handling.
const default_state = {
    application_status: {
        id: null,
        saving: false,
        loading: false,
        loaded: false,
        dirty: false,
        disable_save: false,
        locked: true,
    },
    selected_application: null,
    auction: null,
    authenticated,
    user: null,
    work_basket: null,
    auction_basket: {
        applications: [],
        subscribe_status: false,
        isBroker: false,
        isSuperAdmin: false,
        postcode: null
    },
    mql_auction_basket: {
        applications: [],
        subscribe_status: false,
        isBroker: false,
        isSuperAdmin: false,
        postcode: null
    },
    distribution_basket: {
        applications: [],
        subscribe_status: false,
        isBroker: false,
        isSuperAdmin: false,
    },
    auction_lead_basket: {
        applications: [],
    },
    advance_search_application: {
        isPipelineViewOn: false,
        show : false,
        active : false,
        agents: [],
        brokers:[],
        stage: [],
        statuses:[],
        lenders:[],
        introducers: [],
        loan_asset_types:[],
        pod : [],
        start_date: moment().add(-30, 'days').format('YYYY-MM-DD'),
        end_date :  moment().format('YYYY-MM-DD'),
        status_start_date: null,
        status_end_date : null,
        pods: [],
        child_pods: [],
        get_empty_broker: false,
        get_empty_agent: false,
        get_empty_introducer: false,
        get_empty_lender: false,
        applicant_type: null,
        model_year: null,
        make: null,
        model: null,
    },
    application_search: {},
    assessment: null,
    ml_assessment: null,
    veda_report: null,
    types: {
        options: options,
        properties: properties,
    },
    doc_templates: {},
    document_types: {},
    document_requests: {},
    administration_permissions: null,
    administration_roles: null,
    administration_users: null,
    administration_pods: null,
    administration_billings : null,
    administration_loan_asset_types: null,
    administration_app_status_types: null,
    current_bid: null,
    audit_logs: null,
    email_audit_logs: null,
    sms_audit_logs: null,
    call_outcome_logs: null,
    administration_brokers: null,
    application_locks: null,
    current_lock: {
        app_id: null,
        messages: null
    },
    ppsr_search_results: null,
    ppsr_status_result: null,
    billings: null,
    selected_bills: [],
    warranty_billings: [],
    application_update_status_dialog: {
        show: false,
        application: null,
        stage: null,
        status: null,
        reason: null,
        comment: null,
        send_email: false,
        email_body: null,
    },
    application_update_owners_dialog: {
        show: false,
        application: null,
        all_owners: [],
        query: '',
        role: null,
        results: null,
        selected: null,
    },
    veda_summary_dialog: {
        show: false,
        application_id: null
    },
    application_comment_dialog: {
        show: false,
        application: null
    },
    application_lock: {
        duration: 5,
        reminder: 1
    },
    application_quick_response_dialog: {
        show: false,
        application: null,
    },
    employment_directorship_dialog: {
        show: false,
        application: null,
        directorship: [],
        selected: null
    },
    application_referrer: {
        is_marketing_lead: null,
        referrer: null,
        commission: [],
        show: false,
    },
    uploadedDocuments: null,
    land_title_search_results: null,
    makers: [],
    all_makers: [],
    models: [],
    models_make: [],
    glasses: [],
    administration_lenders: null,
    administration_introducers: null,
    administration_glass_prices: null,
    introducers: null,
    reporting_meta_date: null,
    pre_graph_data: null,
    active_apps_graph_data: null,
    reporting_meta_date_settlement_based: null,
    reporting_meta_date_app_based: null,
    administration_lenders_breakdown: null,
    lead_requests:[],
    lead_requests_forecasts:true,
    fin1_uploaded_document: null,
    toggleSubmitToLenderForm: null,
    error_fields: [],
    is_loan_details_requested_amount_tab_key_pressed: false,
    administration_agents: null,
    all_types_agents: null,
    is_editing: false,
    nextActionDateData: {},
    is_ram_authorized: false,
    broker_postcode: null,
    broker_radius: null,
    agent_has_visibility_subscription: null,
    cut_off_day: null,
    isPodManager: false,
    edit_user_profile_dialog: {
        show: false,
        title: '',
        disable_postcode: false,
        disable_state: false,
    },
    auction_notification: {
        auction_item: null,
        sql_auction_count: 0,
        mql_auction_count: 0,
        live_auction_count: 0,
        app_url: '',
    },
    last_auction_notification_timestamp: 0,
    pagination: {
        backend_pagination: true,
        per_page: 50,
        page: 1
    },
    hem_living_expense_access: false,
    hem_living_expense: [],
    show_live_auction_drawer: false,
    live_auction_previews: [],
    preliminary_assessment_documents: [],
    system_messages: {
        clone_application: {
            title: "Clone Application",
            message: "Are you sure you want to clone this application? Select attachments to clone with applicant details:",
            confirmText: "Clone",
            cancelText: "Cancel",
            waitingText: "Please wait while we are cloning application"
        }
    },
    updating_application: false,
    selected_pipeline_columns: []
};

// list all keys from default_state that need to be reset on different events
const reset_keys_on_events = {
    // an application is loaded
    on_application_load: [
        'selected_application',
        'assessment',
        'ml_assessment',
        'document_requests',
        'audit_logs',
        'application_referrer',
        'application_update_status_dialog',
        'application_update_owners_dialog',
        'employment_directorship_dialog',
        'application_comment_dialog',
        'pagination'
    ],
    // an application is saved
    on_application_save: [
        'work_basket',
        'application_search',
        'assessment',
        'ml_assessment',
        'document_requests',
        'audit_logs',
        'application_referrer',
        'pagination'
    ]
};

export const store = new Vuex.Store({
    state: { ...default_state },
    getters,
    mutations,
    actions,
    plugins: [
        createPersistedState({
            paths: ['nextActionDateData', 'user', 'selected_pipeline_columns'], // States to persist
        }),
    ],
    modules: {
        journey,
        notifications,
    },
})

function addStoreWatcher(state_key, key, callback) {
    let _options;

    let fn = (state, getters) => ({ state, getters })[state_key][key];

    if (typeof callback !== 'function') {
        let { handler, getter, ...options } = callback;
        callback = handler;
        _options = options;
    }

    let _callback = (after, before) => callback(after, before, store);
    store.watch(fn, _callback, _options);
}

for (let [state_key, _watchers] of Object.entries(watchers)) {
    for (let [key, callback] of Object.entries(_watchers)) {
        addStoreWatcher(state_key, key, callback);
    }
}

export { default_state, reset_keys_on_events };
